import React, { useContext, useEffect, useState } from "react";
import PagePreview from "./Preview";
import { useSelector } from "react-redux";
import { FullGridContext } from "..";
import PageContainer from "~/components/PageContainer";
import api from "~/services/api";
import { format } from "date-fns";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

const formatNumber = (value) =>
  typeof value === "number" && !Number.isNaN(value)
    ? value.toLocaleString("pt-BR")
    : "—";

const SmallStatCard = ({ label, value, color, active, onClick }) => (
  <div
    className={`flex flex-col items-center px-4 py-2 cursor-pointer rounded-md transition-colors ${active ? "bg-gray-100 ring-1 ring-gray-300" : "hover:bg-gray-50"}`}
    onClick={onClick}
  >
    <span className="text-[11px] text-gray-500 uppercase tracking-wide mb-1">{label}</span>
    <span className={`text-2xl font-bold ${color || "text-gray-800"}`}>{value}</span>
  </div>
);

const EscalatedStatCard = ({ value, academicStep, active, onClick }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div
      className={`relative flex flex-col items-center px-4 py-2 cursor-pointer rounded-md transition-colors ${active ? "bg-gray-100 ring-1 ring-gray-300" : "hover:bg-gray-50"}`}
      onClick={onClick}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <span className="text-[11px] text-gray-500 uppercase tracking-wide mb-1 underline decoration-dotted">
        Escalated to Acad
      </span>
      <span className="text-2xl font-bold text-purple-600">{formatNumber(value)}</span>

      {visible && academicStep && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 w-48 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs text-gray-700">
          <p className="font-semibold text-gray-800 mb-2 text-[11px] uppercase tracking-wide">Academic Step</p>
          <div className="flex justify-between py-0.5"><span className="text-gray-500">Total</span><span className="font-medium">{formatNumber(academicStep.total ?? 0)}</span></div>
          <div className="flex justify-between py-0.5"><span className="text-amber-600">Pending</span><span className="font-medium">{formatNumber(academicStep.pending ?? 0)}</span></div>
          <div className="flex justify-between py-0.5"><span className="text-amber-500">In Progress</span><span className="font-medium">{formatNumber(academicStep.in_progress ?? 0)}</span></div>
          <div className="flex justify-between py-0.5"><span className="text-green-600">Approved</span><span className="font-medium">{formatNumber(academicStep.approved ?? 0)}</span></div>
          <div className="flex justify-between py-0.5"><span className="text-red-500">Denied</span><span className="font-medium">{formatNumber(academicStep.denied ?? 0)}</span></div>
          <div className="flex justify-between py-0.5"><span className="text-blue-500">More Info</span><span className="font-medium">{formatNumber(academicStep.more_info ?? 0)}</span></div>
          <div className="absolute bottom-[-6px] left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-r border-b border-gray-200 rotate-45" />
        </div>
      )}
    </div>
  );
};

const PIE_COLORS = {
  "In Progress": "#f59e0b",
  "Approved": "#22c55e",
  "Denied": "#ef4444",
  "More Info": "#3b82f6",
  "Escalated to Acad": "#8b5cf6",
};

const buildPieData = (d, isDSO) => [
  { name: "In Progress", value: d?.in_progress ?? 0 },
  { name: "Approved", value: d?.approved ?? 0 },
  { name: "Denied", value: d?.denied ?? 0 },
  { name: "More Info", value: d?.more_info ?? 0 },
  ...(isDSO ? [{ name: "Escalated to Acad", value: d?.escalated_to_acad ?? 0 }] : []),
];

const toISODate = (date) => {
  const d = new Date(date);
  d.setHours(12, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};

// maps card/pie name → query params sent to the table API
const STATUS_FILTER_MAP = {
  "In Progress":       { status: "In Progress" },
  "Approved":          { status: "Approved" },
  "Denied":            { status: "Denied" },
  "More Info":         { status: "More Info" },
  "Escalated to Acad": { current_step: "Academic" },
};

export default function AbsenceExcuses() {
  const filial = useSelector((state) => state.auth.filial);

  const [fromDate, setFromDate] = useState(() => {
    const now = new Date();
    return toISODate(new Date(now.getFullYear(), now.getMonth(), 1));
  });
  const [toDate, setToDate] = useState(() => {
    const now = new Date();
    return toISODate(new Date(now.getFullYear(), now.getMonth() + 1, 0));
  });
  const [summary, setSummary] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null); // name of active card filter

  useEffect(() => {
    async function fetchSummary() {
      try {
        const params = new URLSearchParams({ from_date: fromDate, to_date: toDate });
        if (filial?.id) params.append("filial_id", filial.id);
        const { data } = await api.get(`/medical_excuse_request/summary?${params.toString()}`);
        setSummary(data);
      } catch {
        setSummary(null);
      }
    }
    fetchSummary();
  }, [fromDate, toDate, filial]);

  const isDSO = summary?.viewer_role === "DSO" || summary?.viewer_role === "Holding";
  const title = isDSO ? "Absence Excuses — DSO" : summary?.viewer_role === "Academic" ? "Absence Excuses — Academic" : "Absence Excuses";
  const pieData = buildPieData(summary, isDSO);

  const handleFilterClick = (name) => {
    setActiveFilter((prev) => (prev === name ? null : name));
  };

  const handlePieClick = (data) => {
    if (data?.name) handleFilterClick(data.name);
  };

  const headerContent = (
    <div className="w-full px-0 pb-4 border-b border-gray-100">
      <h3 className="text-sm font-semibold text-gray-700 mb-4">{title}</h3>
      <div className="flex flex-wrap items-center gap-6">
        <div style={{ width: 88, height: 88, flexShrink: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={24}
                outerRadius={40}
                paddingAngle={2}
                dataKey="value"
                onClick={handlePieClick}
                style={{ cursor: "pointer" }}
              >
                {pieData.map((entry) => (
                  <Cell
                    key={entry.name}
                    fill={PIE_COLORS[entry.name] || "#94a3b8"}
                    opacity={activeFilter && activeFilter !== entry.name ? 0.35 : 1}
                  />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center divide-x divide-gray-200">
            <SmallStatCard
              label="Total"
              value={formatNumber(summary?.total ?? 0)}
              active={!activeFilter}
              onClick={() => setActiveFilter(null)}
            />
            <SmallStatCard
              label="In Progress"
              value={formatNumber(summary?.in_progress ?? 0)}
              color="text-amber-600"
              active={activeFilter === "In Progress"}
              onClick={() => handleFilterClick("In Progress")}
            />
            <SmallStatCard
              label="Approved"
              value={formatNumber(summary?.approved ?? 0)}
              color="text-green-600"
              active={activeFilter === "Approved"}
              onClick={() => handleFilterClick("Approved")}
            />
            <SmallStatCard
              label="Denied"
              value={formatNumber(summary?.denied ?? 0)}
              color="text-red-500"
              active={activeFilter === "Denied"}
              onClick={() => handleFilterClick("Denied")}
            />
            <SmallStatCard
              label="More Info"
              value={formatNumber(summary?.more_info ?? 0)}
              color="text-blue-500"
              active={activeFilter === "More Info"}
              onClick={() => handleFilterClick("More Info")}
            />
            {isDSO && (
              <EscalatedStatCard
                value={summary?.escalated_to_acad ?? 0}
                academicStep={summary?.academic_step}
                active={activeFilter === "Escalated to Acad"}
                onClick={() => handleFilterClick("Escalated to Acad")}
              />
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Submitted from:</span>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-mila_orange"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">to</span>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-mila_orange"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const defaultOrderBy = { column: "created_at", asc: false };
  const defaultGridHeader = [
    { title: "Token",           name: "token",               type: "text", filter: false },
    { title: "Student",         name: "student",             type: "text", filter: false },
    { title: "Registration #",  name: "registration_number", type: "text", filter: false },
    { title: "Start Date",      name: "start_date",          type: "text", filter: false },
    { title: "End Date",        name: "end_date",            type: "text", filter: false },
    { title: "Status",          name: "status",              type: "text", filter: false },
    { title: "Current Step",    name: "current_step",        type: "text", filter: false },
    { title: "Submitted on",    name: "created_at",          type: "text", filter: false },
  ];

  const {
    accessModule,
    opened,
    limit,
    orderBy,
    page,
    search,
    setGridData,
    setGridDetails,
    setLoadingData,
    setPages,
  } = useContext(FullGridContext);

  const pageAccess = accessModule.children.find(
    (el) => el.alias === "medical_request"
  );

  async function loader() {
    setLoadingData(true);
    try {
      const params = new URLSearchParams();
      if (search?.value) params.append("search", search.value);
      if (fromDate) params.append("from_date", fromDate);
      if (toDate) params.append("to_date", toDate);
      if (filial?.id) params.append("filial_id", filial.id);

      if (activeFilter && STATUS_FILTER_MAP[activeFilter]) {
        const filterParams = STATUS_FILTER_MAP[activeFilter];
        Object.entries(filterParams).forEach(([k, v]) => params.append(k, v));
      }

      const { data } = await api.get(`/medical_excuse_request?${params.toString()}`);

      const rows = Array.isArray(data) ? data : data?.rows ?? [];
      const total = Array.isArray(data) ? data.length : data?.totalRows ?? rows.length;

      setPages(Math.ceil(total / limit) || 1);
      setGridDetails({ totalRows: total, pages: Math.ceil(total / limit) || 1 });

      const gridDataValues = rows.map(
        ({ id, token, student, start_date, end_date, status, current_step, created_at }, index) => {
          const studentName = student
            ? `${student.name} ${student.last_name}`
            : "-";
          const registrationNumber = student?.registration_number || "-";

          return {
            show: true,
            id,
            fields: [
              token ?? "-",
              studentName,
              registrationNumber,
              start_date ? format(new Date(start_date + "T00:00:00"), "MM/dd/yyyy") : "-",
              end_date ? format(new Date(end_date + "T00:00:00"), "MM/dd/yyyy") : "-",
              status,
              current_step || "-",
              created_at ? format(new Date(created_at), "MM/dd/yyyy @ HH:mm") : "-",
            ],
            selectable: true,
            page: Math.ceil((index + 1) / limit),
          };
        }
      );

      setGridData(gridDataValues);
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingData(false);
    }
  }

  useEffect(() => {
    loader();
  }, [opened, filial, orderBy, search, limit, page, fromDate, toDate, activeFilter]);

  return (
    <PageContainer
      FullGridContext={FullGridContext}
      PagePreview={PagePreview}
      pageAccess={pageAccess}
      defaultGridHeader={defaultGridHeader}
      handleNew={true}
      headerContent={headerContent}
    />
  );
}
